import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { clearUserDrafts } from '@/ui/draftStorage';

import { api } from '../client';
import { queryKeys } from './keys';
import type { Session } from '../types';

export function useSessionQuery() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api<Session>('/api/auth/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/** Which sign-in methods the deployment offers (Microsoft when configured). */
export function useProviders() {
  return useQuery({
    queryKey: queryKeys.providers,
    queryFn: () => api<{ entra: boolean }>('/api/auth/providers'),
    retry: false,
    staleTime: Infinity,
  });
}

export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    meta: { quiet: true },
    mutationFn: (body: { email: string; password: string }) =>
      api<Session>('/api/auth/login', 'POST', body),
    onSuccess: (session) => client.setQueryData(queryKeys.session, session),
  });
}

export function useSignOut() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    meta: { quiet: true },
    mutationFn: () => api<void>('/api/auth/logout', 'POST'),
    // Unsaved document edits kept in this browser are the person's: none stay on a shared device.
    onMutate: () => {
      const userId = client.getQueryData<Session | null>(queryKeys.session)?.user.id;
      if (userId) clearUserDrafts(userId);
    },
    // End the session in place (the provider watches this query), then drop everyone else's data.
    // clear() would also drop the watched session query, leaving the screen signed in.
    // Land on the signed-out card, not /signin: that would go straight back through Microsoft,
    // whose own session is still alive, and sign the person in again.
    onSettled: () => {
      navigate('/signin?signed_out=1', { replace: true });
      client.setQueryData(queryKeys.session, null);
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== queryKeys.session[0] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    meta: { success: 'Password changed' },
    mutationFn: (body: { current_password: string; new_password: string }) =>
      api<void>('/api/auth/password', 'POST', body),
  });
}
